// Campo del código de descuento con su lector QR; lo usa «Elige el teléfono».
import { useState, type ReactNode } from 'react';
import { QrCode } from 'lucide-react';
import { Button, Input } from '@/kit';
import { useDiscountCodeScanner } from '../hooks/useDiscountCodeScanner';
import { normalizeDiscountCode } from '../lib/discountCodeText';

/** Campo del código con su lector QR: normaliza lo tecleado y lo entrega con `onApply`. */
export function DiscountCodeEntry({
    initialText = '',
    label,
    isApplying = false,
    disabled = false,
    error,
    notice,
    onApply,
    onEdit,
}: {
    initialText?: string;
    /** Va a la izquierda del campo, p. ej. «¿Código?». */
    label?: ReactNode;
    isApplying?: boolean;
    /** Venta cobrada o algo en curso: no se escribe ni se aplica. */
    disabled?: boolean;
    error?: string | null;
    notice?: string | null;
    onApply: (code: string) => void;
    /** El vendedor cambió el texto: el error de antes ya no aplica. */
    onEdit?: () => void;
}) {
    const scanner = useDiscountCodeScanner();
    const [text, setText] = useState(initialText);
    const [emptyError, setEmptyError] = useState(false);
    const submit = (): void => {
        const code = normalizeDiscountCode(text);
        setEmptyError(code === '');
        if (code && !disabled) onApply(code);
    };

    return (
        <form
            className="flex flex-col gap-1.5"
            onSubmit={(e) => {
                e.preventDefault();
                submit();
            }}
        >
            <div className="flex items-start gap-2">
                {label}
                <Input
                    aria-label="Código de descuento"
                    placeholder="Escribe el código del cliente"
                    value={text}
                    disabled={disabled}
                    autoCapitalize="characters"
                    onChange={(e) => {
                        setText(e.target.value);
                        setEmptyError(false);
                        onEdit?.();
                    }}
                />
                {scanner.available && (
                    <Button
                        type="button"
                        variant="outline"
                        aria-label="Escanear QR"
                        disabled={disabled}
                    >
                        <QrCode className="size-4" aria-hidden />
                    </Button>
                )}
                <Button type="submit" isLoading={isApplying} disabled={disabled}>
                    Aplicar
                </Button>
            </div>
            {(emptyError || error) && (
                <p role="alert" className="text-[12px] text-destructive">
                    {emptyError ? 'Escribe un código.' : error}
                </p>
            )}
            {!emptyError && !error && notice && (
                <p role="status" className="text-[12px] text-warning">
                    {notice}
                </p>
            )}
        </form>
    );
}
